import { useQuery } from "@tanstack/react-query";
import { abroadStudentApi } from "../../api/abroadLoans";
import { problemOf } from "../../api/scholarships";
import { AMOUNTS, amountOf } from "../scholarships/funding";
import { ABROAD_KEY } from "./model";

// Shared by the student's abroad loan screens
export const ABROAD_STUDENT_KEY = [...ABROAD_KEY, "student"];
export const myAbroadApplicationKey = (id) => [...ABROAD_STUDENT_KEY, "application", id];

export const abroadApplicationPath = (id) => `/my-applications/abroad/${id}`;
export const abroadCallPath = (id) => `/loans/abroad/calls/${id}`;

export const ELIGIBILITY_LABELS = {
  CURRENT_LEVEL: "Current level of study",
  FIELD_OF_STUDY: "Field of study",
  MIN_GRADE_PERCENT: "Minimum final grade",
};

// "Minimum final grade: 70%", or a level or field by name
export const ruleLabel = (rule) => {
  if (rule.kind === "MIN_GRADE_PERCENT") return `${ELIGIBILITY_LABELS[rule.kind]}: ${rule.value}%`;
  return `${ELIGIBILITY_LABELS[rule.kind]}: ${rule.valueName ?? rule.value}`;
};

// Rules of one kind are alternatives: "Current level of study: Bachelor's or Master's"
export const rulesSummary = (rules = []) => {
  const kinds = [...new Set(rules.map((r) => r.kind))];
  return kinds.map((kind) => {
    const ofKind = rules.filter((r) => r.kind === kind);
    if (ofKind.length === 1) return ruleLabel(ofKind[0]);
    return `${ELIGIBILITY_LABELS[kind]}: ${ofKind.map((r) => r.valueName ?? r.value).join(" or ")}`;
  });
};

// The open listed calls, signed in or not (the landing page)
export const useOpenAbroadCalls = ({ signedIn = true } = {}) =>
  useQuery({
    queryKey: [...ABROAD_STUDENT_KEY, "calls", signedIn],
    staleTime: signedIn ? 0 : 5 * 60 * 1000,
    queryFn: async () => (await (signedIn ? abroadStudentApi.calls() : abroadStudentApi.publicCalls())).data,
  });

export const useStudentAbroadCall = (id) =>
  useQuery({
    queryKey: [...ABROAD_STUDENT_KEY, "call", id],
    enabled: Boolean(id),
    retry: (count, error) => problemOf(error)?.status !== 404 && count < 2,
    queryFn: async () => (await abroadStudentApi.call(id)).data,
  });

export const useMyAbroadApplications = () =>
  useQuery({
    queryKey: [...ABROAD_STUDENT_KEY, "applications"],
    queryFn: async () => (await abroadStudentApi.applications()).data,
  });

export const useMyAbroadApplication = (id) =>
  useQuery({
    queryKey: myAbroadApplicationKey(id),
    enabled: Boolean(id),
    queryFn: async () => (await abroadStudentApi.application(id)).data,
  });

export const useAbroadConsent = () =>
  useQuery({
    queryKey: [...ABROAD_STUDENT_KEY, "consent"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => (await abroadStudentApi.consent()).data,
  });

const text = (value) => (value == null ? "" : String(value));

// The form as inputs hold it (strings), from the application
export const formOf = (a) => {
  const loan = a.loan ?? {};
  return {
    admittedInstitution: a.admission?.institution ?? "",
    admittedProgramme: a.admission?.programme ?? "",
    studyCountryCode: a.admission?.countryCode ?? "",
    courseSector: a.courseSector ?? "",
    employed: a.employed == null ? "" : a.employed ? "yes" : "no",
    employer: a.employer ?? "",
    currency: loan.currency ?? "",
    ...Object.fromEntries(AMOUNTS.map(({ key }) => [key, text(loan[key])])),
    studyStartDate: loan.studyStartDate ?? "",
    studyEndDate: loan.studyEndDate ?? "",
    started: loan.coveredPeriodMonths != null || loan.remainingPeriodMonths != null ? "yes" : "no",
    coveredPeriodMonths: text(loan.coveredPeriodMonths),
    remainingPeriodMonths: text(loan.remainingPeriodMonths),
  };
};

// The student's studies, from their academic profile (or as saved, when there is none)
export const backgroundOf = (profile, saved = {}) =>
  profile
    ? {
        currentLevelId: profile.currentLevel?.id ?? null,
        fieldOfStudyId: profile.fieldOfStudy?.id ?? null,
        gradePercent: profile.gradePercent ?? null,
        completionYear: profile.completionYear ?? null,
        previousInstitution: profile.institutionName ?? null,
        previousProgramme: profile.programmeName ?? null,
      }
    : {
        currentLevelId: saved.currentLevelId ?? null,
        fieldOfStudyId: saved.fieldOfStudyId ?? null,
        gradePercent: saved.gradePercent ?? null,
        completionYear: saved.completionYear ?? null,
        previousInstitution: saved.previousInstitution ?? null,
        previousProgramme: saved.previousProgramme ?? null,
      };

// The whole form the API takes on every save (a field left out is cleared): the studies come from the profile
export const formBodyOf = (form, programmeId, background) => ({
  programmeId,
  ...background,
  admittedInstitution: form.admittedInstitution.trim() || null,
  admittedProgramme: form.admittedProgramme.trim() || null,
  studyCountryCode: form.studyCountryCode || null,
  employed: form.employed === "" ? null : form.employed === "yes",
  employer: form.employed === "yes" ? form.employer.trim() || null : null,
  courseSector: form.courseSector || null,
  currency: form.currency || null,
  ...Object.fromEntries(AMOUNTS.map(({ key }) => [key, amountOf(form[key])])),
  studyStartDate: form.studyStartDate || null,
  studyEndDate: form.studyEndDate || null,
  coveredPeriodMonths: form.started === "yes" ? amountOf(form.coveredPeriodMonths) : null,
  remainingPeriodMonths: form.started === "yes" ? amountOf(form.remainingPeriodMonths) : null,
});
